import { trackerDelete, trackerDownload, trackerGet, trackerPatch, trackerPost } from '../client';
import type {
  BreakdownRow,
  ClickEvent,
  LinkCreate,
  LinkStatsItem,
  LinkUpdate,
  PageResult,
  StatsSummary,
  TrackerLink,
} from '../types';

export function listLinks(params: {
  page?: number;
  page_size?: number;
  domain_id?: number;
  group_id?: number;
  q?: string;
  /** Filter by link created_at (inclusive). */
  from?: string;
  to?: string;
} = {}) {
  return trackerGet<PageResult<TrackerLink>>('/links', params);
}

export function getLink(id: number) {
  return trackerGet<TrackerLink>(`/links/${id}`);
}

export function createLink(body: LinkCreate) {
  return trackerPost<TrackerLink>('/links', body);
}

export function copyLink(id: number) {
  return trackerPost<TrackerLink>(`/links/${id}/copy`, {});
}

export function updateLink(id: number, body: LinkUpdate) {
  return trackerPatch<TrackerLink>(`/links/${id}`, body);
}

export function deleteLink(id: number) {
  return trackerDelete(`/links/${id}`);
}

export function linksStats(params: {
  from?: string;
  to?: string;
  link_ids?: string;
}) {
  return trackerGet<{ items: LinkStatsItem[] }>('/links/stats', params);
}

export function linkSummary(id: number, params: { from?: string; to?: string } = {}) {
  return trackerGet<StatsSummary>(`/links/${id}/stats/summary`, params);
}

export function linkBreakdown(
  id: number,
  params: {
    from?: string;
    to?: string;
    group_by?: string;
    country?: string;
    language?: string;
    device_type?: string;
  } = {},
) {
  return trackerGet<{ items: BreakdownRow[] }>(`/links/${id}/stats/breakdown`, params);
}

export function linkClicks(
  id: number,
  params: {
    from?: string;
    to?: string;
    page?: number;
    page_size?: number;
    outcome?: string;
    is_bot?: number;
    country?: string;
    language?: string;
    device_type?: string;
    correlation_id?: string;
    aggregator_id?: number;
  } = {},
) {
  return trackerGet<PageResult<ClickEvent>>(`/links/${id}/clicks`, params);
}

export function linkClicksCsv(
  id: number,
  params: {
    from?: string;
    to?: string;
    outcome?: string;
    is_bot?: number;
    country?: string;
    language?: string;
    device_type?: string;
    aggregator_id?: number | string;
    variant?: 'basic' | 'full';
  } = {},
  filename?: string,
) {
  const variant = params.variant || 'basic';
  const suffix = variant === 'basic' ? '' : `-${variant}`;
  return trackerDownload(
    `/links/${id}/clicks/csv`,
    params,
    filename || `link-${id}-clicks${suffix}.csv`,
  );
}

export function linkClicksXlsx(
  id: number,
  params: {
    from?: string;
    to?: string;
    outcome?: string;
    is_bot?: number;
    country?: string;
    language?: string;
    device_type?: string;
    aggregator_id?: number | string;
    variant?: 'basic' | 'full';
  } = {},
  filename?: string,
) {
  const variant = params.variant || 'full';
  const suffix = variant === 'full' ? '' : `-${variant}`;
  return trackerDownload(
    `/links/${id}/clicks/xlsx`,
    params,
    filename || `link-${id}-clicks${suffix}.xlsx`,
  );
}
